import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger'
import { EmailCategory, EmailPriority, EmailStatus } from '../../../enums'

/**
 * Statut de livraison pour un destinataire
 */
export enum RecipientDeliveryStatus {
  PENDING = 'PENDING',
  QUEUED = 'QUEUED',
  SENDING = 'SENDING',
  SENT = 'SENT',
  DELIVERED = 'DELIVERED',
  BOUNCED = 'BOUNCED',
  FAILED = 'FAILED',
}

/**
 * Response DTO pour un destinataire d'email
 */
export class EmailRecipientResponse {
  @ApiProperty({ description: 'ID unique du destinataire' })
  id!: string

  @ApiProperty({ description: "ID de l'email parent" })
  emailId!: string

  @ApiProperty({ description: 'Adresse email' })
  email!: string

  @ApiPropertyOptional({ description: 'Nom du destinataire' })
  name?: string

  @ApiPropertyOptional({ description: "ID de l'utilisateur" })
  userId?: string

  @ApiPropertyOptional({ description: "ID de l'établissement" })
  establishmentId?: string

  @ApiProperty({ enum: RecipientDeliveryStatus, description: 'Statut de livraison' })
  deliveryStatus!: RecipientDeliveryStatus

  @ApiPropertyOptional({ description: "Date d'envoi" })
  sentAt?: Date

  @ApiPropertyOptional({ description: 'Date de livraison' })
  deliveredAt?: Date

  @ApiPropertyOptional({ description: "Date d'ouverture" })
  openedAt?: Date

  @ApiPropertyOptional({ description: 'Date de clic' })
  clickedAt?: Date

  @ApiPropertyOptional({ description: 'Date de bounced' })
  bouncedAt?: Date

  @ApiPropertyOptional({ description: 'Raison du bounce' })
  bounceReason?: string

  @ApiPropertyOptional({ description: "Message d'erreur" })
  lastError?: string

  @ApiProperty({ description: "Nombre de tentatives d'envoi" })
  retryCount!: number
}

/**
 * Response DTO pour un email
 */
export class EmailResponse {
  @ApiProperty({ description: 'ID unique' })
  id!: string

  @ApiProperty({ description: 'Date de création' })
  createdAt!: Date

  @ApiProperty({ description: 'Date de mise à jour' })
  updatedAt!: Date

  @ApiProperty({ description: "ID de l'expéditeur" })
  senderId!: string

  @ApiProperty({ description: "Nom de l'expéditeur" })
  senderName!: string

  @ApiProperty({ description: "Email de l'expéditeur" })
  senderEmail!: string

  @ApiProperty({ description: "Rôle de l'expéditeur" })
  senderRole!: string

  @ApiProperty({ description: 'Sujet' })
  subject!: string

  @ApiProperty({ description: 'Corps texte' })
  body!: string

  @ApiPropertyOptional({ description: 'Corps HTML' })
  bodyHtml?: string

  @ApiProperty({ enum: EmailPriority, description: 'Priorité' })
  priority!: EmailPriority

  @ApiPropertyOptional({ enum: EmailCategory, description: 'Catégorie' })
  category?: EmailCategory

  @ApiPropertyOptional({ description: "ID de l'établissement" })
  establishmentId?: string

  @ApiPropertyOptional({ description: 'Type de ressource liée' })
  relatedType?: string

  @ApiPropertyOptional({ description: 'ID de ressource liée' })
  relatedId?: string

  @ApiPropertyOptional({ description: 'ID du template' })
  templateId?: string

  @ApiPropertyOptional({ description: 'Données du template' })
  templateData?: Record<string, unknown>

  @ApiProperty({ enum: EmailStatus, description: 'Statut' })
  status!: EmailStatus

  @ApiPropertyOptional({ description: 'Date planifiée' })
  scheduledAt?: Date

  @ApiPropertyOptional({ description: "Date d'envoi" })
  sentAt?: Date

  @ApiPropertyOptional({ description: 'ID du job de queue' })
  queueJobId?: string

  @ApiPropertyOptional({ description: "Message d'erreur" })
  lastError?: string

  @ApiProperty({ description: "Nombre de tentatives d'envoi" })
  retryCount!: number

  @ApiProperty({ description: 'Total des destinataires' })
  totalRecipients!: number

  @ApiProperty({ description: 'Nombre envoyés' })
  sentCount!: number

  @ApiProperty({ description: 'Nombre échoués' })
  failedCount!: number

  @ApiProperty({ description: 'Nombre ouverts' })
  openedCount!: number

  @ApiPropertyOptional({ description: 'ID de la campagne parente' })
  campaignId?: string

  @ApiPropertyOptional({ type: [EmailRecipientResponse], description: 'Liste des destinataires' })
  recipients?: EmailRecipientResponse[]
}

/**
 * Réponse paginée d'emails
 */
export class PaginatedEmailsResponse {
  @ApiProperty({ type: [EmailResponse], description: 'Liste des emails' })
  data!: EmailResponse[]

  @ApiProperty({ description: 'Total des résultats' })
  total!: number

  @ApiProperty({ description: 'Page courante' })
  page!: number

  @ApiProperty({ description: 'Limite par page' })
  limit!: number

  @ApiProperty({ description: 'Nombre total de pages' })
  totalPages!: number
}
